import React, { useMemo, useState } from 'react';
import {
  Drawer,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Autocomplete,
  Card,
  CardContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormik, FieldArray, FormikProvider } from 'formik';
import * as Yup from 'yup';
import {
  QueueStrategy,
  QueueStrategyRulePayload,
  QueueStrategyRequest,
} from '../../../api/queueStrategies';

export interface QueueOption {
  id: string;
  name: string;
  code?: string;
}

interface StrategyFormDrawerProps {
  open: boolean;
  onClose: () => void;
  queues: QueueOption[];
  isSubmitting: boolean;
  initialStrategy?: QueueStrategy | null;
  onSubmit: (payload: QueueStrategyRequest) => Promise<void>;
}

interface RuleForm {
  id?: string;
  name: string;
  description?: string;
  weight: number;
  enabled: boolean;
  orderIndex: number;
  jsonLogic: string;
}

interface StrategyFormValues {
  name: string;
  description: string;
  mode: 'scoring' | 'rule_order' | 'hybrid';
  status: 'draft' | 'active' | 'archived';
  defaultPriorityScore: number;
  queueIds: string[];
  metadataJson: string;
  rules: RuleForm[];
}

const defaultRule: RuleForm = {
  name: 'Новое правило',
  description: '',
  weight: 10,
  enabled: true,
  orderIndex: 0,
  jsonLogic: '{"==":[{"var":"ticket.priority"},"vip"]}',
};

const validationSchema = Yup.object({
  name: Yup.string().min(3).required('Введите название стратегии'),
  description: Yup.string().max(2000).nullable(),
  mode: Yup.mixed<'scoring' | 'rule_order' | 'hybrid'>()
    .oneOf(['scoring', 'rule_order', 'hybrid'])
    .required(),
  status: Yup.mixed<'draft' | 'active' | 'archived'>()
    .oneOf(['draft', 'active', 'archived'])
    .required(),
  defaultPriorityScore: Yup.number()
    .min(0, 'Score не может быть меньше 0')
    .max(1000, 'Слишком большой score')
    .required('Укажите базовый score'),
  queueIds: Yup.array().of(Yup.string()).min(1, 'Выберите хотя бы одну очередь'),
  metadataJson: Yup.string(),
  rules: Yup.array().of(
    Yup.object({
      name: Yup.string().required('Название правила обязательно'),
      description: Yup.string().nullable(),
      weight: Yup.number().required('Вес обязателен'),
      enabled: Yup.boolean(),
      jsonLogic: Yup.string().required('JSON Logic обязателен'),
    }),
  ).min(1, 'Добавьте хотя бы одно правило'),
});

const convertRuleToForm = (rule: QueueStrategyRulePayload, index: number): RuleForm => ({
  id: rule.id,
  name: rule.name,
  description: rule.description || '',
  weight:
    typeof rule.weight === 'number'
      ? rule.weight
      : typeof rule.effectPayload?.priorityDelta === 'number'
      ? (rule.effectPayload.priorityDelta as number)
      : 0,
  enabled: rule.enabled !== false,
  orderIndex: rule.orderIndex ?? index,
  jsonLogic: JSON.stringify(rule.condition ?? {}, null, 2),
});

const prettifyJson = (value: string) => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (error) {
    return value;
  }
};

export const StrategyFormDrawer: React.FC<StrategyFormDrawerProps> = ({
  open,
  onClose,
  queues,
  isSubmitting,
  initialStrategy,
  onSubmit,
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const initialValues = useMemo<StrategyFormValues>(() => {
    if (!initialStrategy) {
      return {
        name: '',
        description: '',
        mode: 'scoring',
        status: 'draft',
        defaultPriorityScore: 100,
        queueIds: [],
        metadataJson: '',
        rules: [defaultRule],
      };
    }

    return {
      name: initialStrategy.name,
      description: initialStrategy.description || '',
      mode: initialStrategy.mode,
      status: initialStrategy.status,
      defaultPriorityScore: initialStrategy.defaultPriorityScore,
      queueIds:
        initialStrategy.assignments?.map((assignment) => assignment.queueId) ?? [],
      metadataJson: initialStrategy.metadata
        ? JSON.stringify(initialStrategy.metadata, null, 2)
        : '',
      rules:
        initialStrategy.rules && initialStrategy.rules.length > 0
          ? initialStrategy.rules.map(convertRuleToForm)
          : [defaultRule],
    };
  }, [initialStrategy]);

  const formik = useFormik<StrategyFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      setSubmitError(null);
      try {
        let metadata: Record<string, unknown> | undefined;
        if (values.metadataJson && values.metadataJson.trim()) {
          metadata = JSON.parse(values.metadataJson);
        }

        const rules: QueueStrategyRulePayload[] = values.rules.map((rule, index) => {
          try {
            const condition = JSON.parse(rule.jsonLogic);
            return {
              id: rule.id,
              name: rule.name,
              description: rule.description || null,
              orderIndex: index,
              enabled: rule.enabled,
              condition,
              effectType: 'adjust_priority',
              effectPayload: { priorityDelta: rule.weight },
              weight: rule.weight,
            };
          } catch (error) {
            throw new Error(`Неверный JSON Logic в правиле «${rule.name || `#${index + 1}`}»`);
          }
        });

        const payload: QueueStrategyRequest = {
          name: values.name,
          description: values.description || undefined,
          mode: values.mode,
          status: values.status,
          defaultPriorityScore: values.defaultPriorityScore,
          queueIds: values.queueIds,
          rules,
          metadata,
        };

        await onSubmit(payload);
        helpers.setSubmitting(false);
      } catch (error) {
        helpers.setSubmitting(false);
        if (error instanceof Error) {
          setSubmitError(error.message);
        } else {
          setSubmitError('Не удалось сохранить стратегию');
        }
      }
    },
  });

  const handleClose = () => {
    if (isSubmitting || formik.isSubmitting) {
      return;
    }
    formik.resetForm();
    setSubmitError(null);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{ '& .MuiDrawer-paper': { width: 520, maxWidth: '100%', p: 3 } }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          {initialStrategy ? 'Редактирование стратегии' : 'Новая стратегия'}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2}>
            {submitError && (
            <Alert severity="error" onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          <TextField
            label="Название стратегии"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            fullWidth
            required
          />

          <TextField
            label="Описание"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            fullWidth
            multiline
            minRows={2}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Режим"
              name="mode"
              value={formik.values.mode}
              onChange={formik.handleChange}
              error={formik.touched.mode && Boolean(formik.errors.mode)}
              helperText={formik.touched.mode && formik.errors.mode}
              fullWidth
            >
              <MenuItem value="scoring">Взвешивание</MenuItem>
              <MenuItem value="rule_order">Правила по порядку</MenuItem>
              <MenuItem value="hybrid">Гибрид</MenuItem>
            </TextField>

            <TextField
              select
              label="Статус"
              name="status"
              value={formik.values.status}
              onChange={formik.handleChange}
              error={formik.touched.status && Boolean(formik.errors.status)}
              helperText={formik.touched.status && formik.errors.status}
              fullWidth
            >
              <MenuItem value="draft">Черновик</MenuItem>
              <MenuItem value="active">Активна</MenuItem>
              <MenuItem value="archived">Архив</MenuItem>
            </TextField>
          </Stack>

          <TextField
            label="Базовый score"
            name="defaultPriorityScore"
            type="number"
            value={formik.values.defaultPriorityScore}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.defaultPriorityScore && Boolean(formik.errors.defaultPriorityScore)}
            helperText={formik.touched.defaultPriorityScore && formik.errors.defaultPriorityScore}
            fullWidth
          />

          <Autocomplete
            multiple
            options={queues}
            value={queues.filter((queue) => formik.values.queueIds.includes(queue.id))}
            onChange={(_, value) => formik.setFieldValue('queueIds', value.map((item) => item.id))}
            getOptionLabel={(option) =>
              option.code ? `${option.name} (${option.code})` : option.name
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Очереди"
                error={formik.touched.queueIds && Boolean(formik.errors.queueIds)}
                helperText={formik.touched.queueIds && formik.errors.queueIds}
              />
            )}
            disableCloseOnSelect
            size="small"
          />

          <TextField
            label="Дополнительные настройки (metadata)"
            name="metadataJson"
            value={formik.values.metadataJson}
            onChange={formik.handleChange}
            onBlur={(event) =>
              formik.setFieldValue('metadataJson', prettifyJson(event.target.value))
            }
            multiline
            minRows={4}
            fullWidth
            placeholder='{"weightMatrix": []}'
          />

          <Divider textAlign="left">Правила</Divider>

          <FieldArray
            name="rules"
            render={(arrayHelpers) => (
              <Stack spacing={2}>
                {formik.values.rules.map((rule, index) => {
                  const errors = formik.errors.rules?.[index] || {};
                  const touched = formik.touched.rules?.[index] || {};

                  return (
                    <Card variant="outlined" key={rule.id ?? index}>
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1">Правило {index + 1}</Typography>
                            <IconButton
                              size="small"
                              onClick={() => arrayHelpers.remove(index)}
                              disabled={formik.values.rules.length === 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>

                          <TextField
                            label="Название"
                            name={`rules[${index}].name`}
                            value={rule.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean((errors as any).name) && Boolean((touched as any).name)}
                            helperText={(touched as any).name && (errors as any).name}
                            fullWidth
                          />

                          <TextField
                            label="Описание"
                            name={`rules[${index}].description`}
                            value={rule.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            fullWidth
                            multiline
                            minRows={2}
                          />

                          <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                              label="Вес / Δ score"
                              name={`rules[${index}].weight`}
                              type="number"
                              value={rule.weight}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={Boolean((errors as any).weight) && Boolean((touched as any).weight)}
                              helperText={(touched as any).weight && (errors as any).weight}
                              fullWidth
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={rule.enabled}
                                  onChange={(_, checked) =>
                                    formik.setFieldValue(`rules[${index}].enabled`, checked)
                                  }
                                />
                              }
                              label="Правило активно"
                            />
                          </Stack>

                          <TextField
                            label="JSON Logic"
                            name={`rules[${index}].jsonLogic`}
                            value={rule.jsonLogic}
                            onChange={formik.handleChange}
                            onBlur={(event) =>
                              formik.setFieldValue(
                                `rules[${index}].jsonLogic`,
                                prettifyJson(event.target.value),
                              )
                            }
                            error={Boolean((errors as any).jsonLogic) && Boolean((touched as any).jsonLogic)}
                            helperText={(touched as any).jsonLogic && (errors as any).jsonLogic}
                            multiline
                            minRows={4}
                            fullWidth
                            placeholder='{"==":[{"var":"ticket.priority"},"vip"]}'
                          />
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    arrayHelpers.push({
                      ...defaultRule,
                      orderIndex: formik.values.rules.length,
                    })
                  }
                >
                  Добавить правило
                </Button>
              </Stack>
            )}
          />

          <Divider />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose}>Отмена</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || formik.isSubmitting}
            >
              {isSubmitting || formik.isSubmitting
                ? 'Сохранение...'
                : initialStrategy
                ? 'Сохранить'
                : 'Создать'}
            </Button>
          </Stack>
          </Stack>
        </form>
      </FormikProvider>
    </Drawer>
  );
};

export default StrategyFormDrawer;
